import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Select, Space, Tag, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { Page } from "@zhihaoscm/entity";
import type { Claim } from "@zhihaoscm/entity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { CLAIM_STATE } from "@/meta/logistics/deviceClaim";
import { useDeviceClaimActionAccess } from "./useDeviceClaimActionAccess";

export interface DeviceClaimQuery {
    page: number;
    size: number;
    mmsi?: string;
    shipName?: string;
    customerInfo?: string;
    state?: number;
    isAsc?: number;
}

export interface DeviceClaimPageProps {
    loadClaims?: (query: DeviceClaimQuery) => Promise<Page<Claim>>;
    permissions?: string[];
    banButton?: boolean;
}

// 旧 Vue ProTable 未显式配置时默认每页 20 条，React 迁移保持同一首屏请求。
const defaultQuery: DeviceClaimQuery = { page: 1, size: 10 };

/** 复用 Vue 设备认领列表的 `/claim/paging` 接口。 */
export async function loadDeviceClaims(query: DeviceClaimQuery): Promise<Page<Claim>> {
    const response = await request.service.get<Page<Claim>>("/claim/paging", { params: query });
    return response.data;
}

/** 将认领记录时间格式化为旧页面的分钟级展示。 */
const formatClaimDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

/** 将承运商账号信息保持为 Vue 页面中的三段式文案。 */
const formatCustomer = (customerInfo?: Claim["customerInfo"]) =>
    customerInfo
        ? `${customerInfo.institutionName || customerInfo.realName || DEFAULT_VALUE} | ${customerInfo.mobile || DEFAULT_VALUE} | ${customerInfo.code || DEFAULT_VALUE}`
        : DEFAULT_VALUE;

/** 物流管理-设备认领列表，覆盖 Vue 首屏筛选、分页、状态和处理入口。 */
export function DeviceClaimPage({ loadClaims = loadDeviceClaims, permissions, banButton }: DeviceClaimPageProps) {
    const { canBind, isButtonBanned } = useDeviceClaimActionAccess({ permissions, banButton });
    const [rows, setRows] = useState<Claim[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<DeviceClaimQuery>(defaultQuery);
    const [mmsi, setMmsi] = useState("");
    const [shipName, setShipName] = useState("");
    const [customerInfo, setCustomerInfo] = useState("");
    const [state, setState] = useState<number | undefined>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadClaims(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("设备认领列表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadClaims, query]);

    // 将 Vue ProTable 的草稿筛选值转换为分页接口参数，并将查询页归一到第一页。
    const submitFilters = () => {
        const nextQuery: DeviceClaimQuery = { page: 1, size: query.size };
        if (mmsi) nextQuery.mmsi = mmsi;
        if (shipName) nextQuery.shipName = shipName;
        if (customerInfo) nextQuery.customerInfo = customerInfo;
        if (state !== undefined) nextQuery.state = state;
        setQuery(nextQuery);
    };

    // 清空筛选并恢复 Vue 列表首屏参数。
    const resetFilters = () => {
        setMmsi("");
        setShipName("");
        setCustomerInfo("");
        setState(undefined);
        setQuery(defaultQuery);
    };

    // 保留 Vue ProTable 的创建时间排序参数，映射 zhihao-ui 的方向到旧接口 isAsc 值。
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            if (prop !== "createdTime") return;
            setQuery(current => {
                const nextQuery = { ...current, page: 1 };
                if (!order) delete nextQuery.isAsc;
                else nextQuery.isAsc = order === "ascending" ? 1 : 0;
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 的列契约承接旧 Vue 设备认领 ProTable 字段和处理插槽。
    const columns = useMemo<TableColumnProps<Claim>[]>(
        () => [
            {
                key: "shipId",
                label: "MMSI",
                width: 134,
                prop: "shipId",
                render: ({ row }) =>
                    row.shipId ? <Link to={`/logistics/deviceClaim/info/${row.id}`}>{row.shipId}</Link> : DEFAULT_VALUE
            },
            {
                key: "customerInfo",
                label: "承运商账号",
                minWidth: 148,
                prop: "customerInfo",
                render: ({ row }) => formatCustomer(row.customerInfo)
            },
            {
                key: "shipName",
                label: "船舶",
                minWidth: 176,
                prop: "name",
                render: ({ row }) => {
                    const label = row.shipNameCn
                        ? `${row.shipNameCn} / ${row.shipName || DEFAULT_VALUE}`
                        : row.shipName || DEFAULT_VALUE;
                    return row.shipId ? (
                        <Link to={`/logistics/shipManage/shipInfo/shipInfoDetail/${row.shipId}`}>{label}</Link>
                    ) : (
                        label
                    );
                }
            },
            {
                key: "handlerName",
                label: "处理人",
                minWidth: 78,
                prop: "handlerName",
                render: ({ row }) => row.handlerName || DEFAULT_VALUE
            },
            {
                key: "handleTime",
                label: "处理时间",
                width: 154,
                prop: "handleTime",
                render: ({ row }) => formatClaimDate(row.handleTime)
            },
            {
                key: "createdTime",
                label: "创建时间",
                width: 154,
                prop: "createdTime",
                sortable: true,
                render: ({ row }) => formatClaimDate(row.createdTime)
            },
            {
                key: "state",
                label: "状态",
                width: 134,
                prop: "state",
                render: ({ row }) => (
                    <Tag color={Number(row.state) === CLAIM_STATE.CLAIMED.code ? "default" : "processing"}>
                        {CLAIM_STATE.getIntroByCode(row.state) || DEFAULT_VALUE}
                    </Tag>
                )
            },
            {
                key: "operation",
                label: "操作",
                width: 120,
                prop: "operation",
                fixed: "right",
                render: ({ row }) =>
                    canBind && Number(row.state) === CLAIM_STATE.CLAIMING.code ? (
                        <Link to={`/logistics/deviceClaim/form/${row.id}`}>
                            <Button aria-label="处理" disabled={isButtonBanned} type="link">
                                处理
                            </Button>
                        </Link>
                    ) : null
            }
        ],
        [canBind, isButtonBanned]
    );

    return (
        <section aria-label="device-claim-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col">
                <ZhTable<Claim>
                    rowKey="id"
                    data={rows}
                    columns={normalizeOperationColumns(columns, rows)}
                    pagination={false}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                设备认领
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form className="admin-table-filter-form mb-4" layout="inline" onFinish={submitFilters}>
                                <Form.Item label="MMSI">
                                    <Input
                                        aria-label="MMSI"
                                        maxLength={32}
                                        placeholder="请输入MMSI号搜索"
                                        value={mmsi}
                                        onChange={event => setMmsi(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label="承运商账号">
                                    <Input
                                        aria-label="承运商账号"
                                        placeholder="请选择输入账号信息搜索"
                                        value={customerInfo}
                                        onChange={event => setCustomerInfo(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label="船舶名称">
                                    <Input
                                        aria-label="船舶名称"
                                        maxLength={32}
                                        placeholder="请输入船舶名称搜索"
                                        value={shipName}
                                        onChange={event => setShipName(event.target.value)}
                                    />
                                </Form.Item>
                                <Form.Item label="状态">
                                    <Select
                                        allowClear
                                        aria-label="状态"
                                        placeholder="请选择状态"
                                        value={state}
                                        options={CLAIM_STATE.options.map(option => ({
                                            label: option.label,
                                            value: Number(option.value)
                                        }))}
                                        onChange={value => setState(value)}
                                    />
                                </Form.Item>
                                <Space>
                                    <Button aria-label="查询" htmlType="submit" type="primary">
                                        查询
                                    </Button>
                                    <Button onClick={resetFilters}>重置</Button>
                                </Space>
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                onChange={(page, pageSize) => {
                                    // 旧版 ProTable 切换条数后固定回到第 1 页，普通翻页则保留当前条数。
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }));
                                }}
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}
